import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";

import {
  cinemaHallsApi,
  cinemasApi,
  myLibraryApi,
  subscriptionTypesApi,
  worksApi,
} from "../services/api";
import type { Cinema, CinemaHall, Work } from "../types";
import { RatingSelect } from "../components/RatingSelect";
import { WorkInfoCard } from "../components/WorkInfoCard";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

export function MyLibraryAddMoviePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { canCreate } = usePermissions();
  const [searchParams] = useSearchParams();

  const preselectedWorkId = searchParams.get("work_id");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  // Query for the preselected work if work_id is provided
  const { data: preselectedWork } = useQuery({
    queryKey: ["work", preselectedWorkId],
    queryFn: () => worksApi.get(preselectedWorkId as string),
    enabled: !!preselectedWorkId,
  });

  // Query for library entries to know which movies are already added
  const { data: libraryEntriesData } = useQuery({
    queryKey: ["my-library-entries", "all-movies"],
    queryFn: () =>
      myLibraryApi.listEntries({
        type: "movie",
        page: 1,
        page_size: 1000,
      }),
  });

  const existingWorkIds = useMemo(() => {
    const items = libraryEntriesData?.items ?? [];
    return new Set(items.map((item) => String(item.work_id)));
  }, [libraryEntriesData]);

  // Set the form value when preselected work is loaded
  useEffect(() => {
    if (preselectedWork && preselectedWorkId) {
      form.setFieldValue("work_id", preselectedWorkId);
    }
  }, [preselectedWork, preselectedWorkId, form]);

  const { data: worksData, isLoading: worksLoading } = useQuery({
    queryKey: ["works", "my-library-add-movie", search],
    queryFn: () =>
      worksApi.list({
        page: 1,
        page_size: 50,
        type: "movie",
        search: search || undefined,
        sort: "title",
        order: "asc",
      }),
  });

  const workOptions = useMemo(() => {
    const items = worksData?.items ?? [];
    let filteredItems = items;

    if (!showAll) {
      filteredItems = items.filter((w) => !existingWorkIds.has(String(w.id)));
    }

    const options = filteredItems.map((w: Work) => ({
      value: w.id,
      label: w.title,
    }));

    // Add preselected work to options if not already present
    if (
      preselectedWork &&
      !options.some((opt) => opt.value === preselectedWork.id)
    ) {
      options.unshift({
        value: preselectedWork.id,
        label: preselectedWork.title,
      });
    }

    return options;
  }, [worksData, preselectedWork, showAll, existingWorkIds]);

  const { data: cinemasData, isLoading: cinemasLoading } = useQuery({
    queryKey: ["cinemas", "my-library-add-movie"],
    queryFn: () => cinemasApi.list({ page: 1, page_size: 1000 }),
  });

  const cinemaOptions = useMemo(() => {
    const items = cinemasData?.items ?? [];
    return items.map((c: Cinema) => ({
      value: c.id,
      label: c.city ? `${c.name} (${c.city})` : c.name,
    }));
  }, [cinemasData]);

  const selectedCinemaId = Form.useWatch("cinema_id", form) as
    | string
    | undefined;

  const { data: hallsData, isLoading: hallsLoading } = useQuery({
    queryKey: ["cinema-halls", selectedCinemaId],
    queryFn: () => cinemaHallsApi.listByCinema(selectedCinemaId as string),
    enabled: !!selectedCinemaId,
  });

  const { data: subscriptionTypesData, isLoading: subscriptionTypesLoading } = useQuery({
    queryKey: ["subscription-types"],
    queryFn: () => subscriptionTypesApi.list(),
  });

  const subscriptionTypeOptions = useMemo(() => {
    const items = subscriptionTypesData?.items ?? [];
    return items
      .filter((st) => st.is_active)
      .map((st) => ({ value: st.id, label: st.name }));
  }, [subscriptionTypesData]);

  const hallOptions = useMemo(() => {
    const items = hallsData ?? [];
    return items.map((h: CinemaHall) => ({ value: h.id, label: h.hall_name }));
  }, [hallsData]);

  const selectedWorkId = Form.useWatch("work_id", form) as string | undefined;

  const mutation = useMutation({
    mutationFn: (payload: {
      work_id: string;
      finished_at: string;
      rating?: number;
      review?: string;
      private_notes?: string | null;
      cinema_id: string;
      cinema_hall_id?: string;
      screening_at: string;
      ticket_price?: number;
      is_subscription?: boolean;
      subscription_type_id?: string;
      seat_row?: string;
      seat_number?: string;
      is_public?: boolean;
    }) => myLibraryApi.createEntry(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-library-years"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-stats"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-monthly"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-entries"] });
      message.success("Wpis dodany");
      navigate("/");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || "Nie udało się dodać wpisu";
      message.error(errorMessage);
    },
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/my-library/add")}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          Dodaj film (seans)
        </Title>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            const screeningLocal = values.screening_at as string;
            const screeningIso = screeningLocal
              ? new Date(screeningLocal).toISOString()
              : undefined;

            if (!screeningIso) {
              message.error("Wybierz datę i godzinę seansu");
              return;
            }

            mutation.mutate({
              work_id: values.work_id as string,
              finished_at: values.finished_at as string,
              rating: values.rating as number | undefined,
              review: values.review as string | undefined,
              private_notes: values.private_notes as string | null | undefined,
              cinema_id: values.cinema_id as string,
              cinema_hall_id: values.cinema_hall_id as string | undefined,
              screening_at: screeningIso,
              ticket_price: values.ticket_price as number | undefined,
              is_subscription: values.is_subscription as boolean | undefined,
              subscription_type_id: values.subscription_type_id as string | undefined,
              seat_row: values.seat_row as string | undefined,
              seat_number: values.seat_number as string | undefined,
              is_public: values.is_public,
            });
          }}
          initialValues={{
            finished_at: new Date().toISOString().slice(0, 10),
            is_public: false,
          }}
        >
          <Form.Item
            name="work_id"
            label="Film"
            rules={[{ required: true, message: "Wybierz film" }]}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <Select
                showSearch
                placeholder="Wybierz film"
                options={workOptions}
                loading={worksLoading}
                filterOption={false}
                onSearch={(v) => setSearch(v)}
                onChange={(value) => {
                  form.setFieldValue("work_id", value);
                  form.validateFields(["work_id"]);
                }}
                allowClear
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space>
                  <Switch
                    size="small"
                    checked={showAll}
                    onChange={(checked) => setShowAll(checked)}
                  />
                  <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
                    Pokaż już dodane
                  </span>
                </Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate("/movies/new")}
                  style={{ padding: 0 }}
                >
                  + Dodaj nowy film do bazy
                </Button>
              </div>
            </div>
          </Form.Item>

          {selectedWorkId && <WorkInfoCard workId={selectedWorkId} />}

          <Form.Item
            name="cinema_id"
            label="Kino"
            rules={[{ required: true, message: "Wybierz kino" }]}
          >
            <Select
              placeholder="Wybierz kino"
              options={cinemaOptions}
              loading={cinemasLoading}
              allowClear
              onChange={() => {
                form.setFieldValue("cinema_hall_id", undefined);
              }}
            />
          </Form.Item>

          <Form.Item
            name="cinema_hall_id"
            label="Sala"
            rules={[{ required: true, message: "Wybierz salę" }]}
          >
            <Select
              placeholder={
                selectedCinemaId ? "Wybierz salę" : "Najpierw wybierz kino"
              }
              options={hallOptions}
              loading={hallsLoading}
              disabled={!selectedCinemaId}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="screening_at"
            label="Data i godzina seansu"
            rules={[{ required: true, message: "Podaj datę i godzinę seansu" }]}
          >
            <Input type="datetime-local" />
          </Form.Item>

          <Form.Item
            name="finished_at"
            label="Data ukończenia"
            rules={[{ required: true, message: "Data jest wymagana" }]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item name="ticket_price" label="Cena biletu">
            <InputNumber min={0} step={0.5} style={{ width: 200 }} />
          </Form.Item>

          <Form.Item
            name="is_subscription"
            label="Abonament"
            valuePropName="checked"
          >
            <Switch checkedChildren="Tak" unCheckedChildren="Nie" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.is_subscription !== currentValues.is_subscription
            }
          >
            {({ getFieldValue }) =>
              getFieldValue("is_subscription") ? (
                <Form.Item name="subscription_type_id" label="Typ abonamentu">
                  <Select
                    placeholder="Wybierz typ abonamentu"
                    options={subscriptionTypeOptions}
                    loading={subscriptionTypesLoading}
                    style={{ width: 300 }}
                    allowClear
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item name="seat_row" label="Rząd">
            <Input style={{ width: 200 }} />
          </Form.Item>

          <Form.Item name="seat_number" label="Miejsce">
            <Input style={{ width: 200 }} />
          </Form.Item>

          <Form.Item name="rating" label="Ocena">
            <RatingSelect />
          </Form.Item>

          <Form.Item name="review" label="Notatka">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="private_notes" label="Prywatne notatki">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="is_public"
            label="Opublikowany"
            valuePropName="checked"
            tooltip="Gdy włączone, recenzja będzie widoczna w publicznej bibliotece"
          >
            <Switch checkedChildren="Tak" unCheckedChildren="Nie" />
          </Form.Item>

          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate("/")}>Anuluj</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={mutation.isPending}
                disabled={!canCreate}
                title={!canCreate ? "Brak uprawnień do dodawania" : undefined}
              >
                Dodaj
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default MyLibraryAddMoviePage;
